// @flow
// -----------------------------------------------------------------
// <copyright
//   file="Izenda.ConnectionRestEndpoint.js"
//   company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// -----------------------------------------------------------------

import { observable, action } from 'mobx';
import IzendaRequest, { IZENDA_REQUEST_BODY_LANGUAGES, IzendaRequestHeader } from './Izenda.Request';
import localizer from 'share/localization/Izenda.Localizer';
import { isNil, reduce, keys, find, map, each } from "lodash/fp";

const DEFAULT_DEPTH = 4;
const DEFAULT_SEPARATOR = '.';

/**
 * Rest connection endpoint model
 */
export default class ConnectionRestEndpoint {

  /**
   * Constructor
   * @param {boolean} requestCacheEnabled Real cache enabled value
   * @param {number} requestCacheTtl Real cache ttl value
   * @param {boolean} defaultCacheEnabled Default cache enabled value
   * @param {number} defaultCacheTtl Default cache ttl value
   */
  constructor(requestCacheEnabled, requestCacheTtl, defaultCacheEnabled, defaultCacheTtl) {
    this.requestCacheEnabled = requestCacheEnabled;
    this.requestCacheTtl = requestCacheTtl;
    this.defaultCacheEnabled = defaultCacheEnabled;
    this.defaultCacheTtl = defaultCacheTtl;
  }

  /**
   * Default cache enabled value
   * @type {boolean}
   */
  defaultCacheEnabled = true;

  /**
   * Default TTL value
   * @type {number}
   */
  defaultCacheTtl = 600;

  /**
   * Endpoint name
   * @type {string}
   */
  @observable endpointName = null;

  /**
   * Endpoint URL
   * @type {string}
   */
  @observable address = null;

  /**
   * Endpoint scheme path. Can contain following items:
   * - external API URL path
   * - list of query source names which will be passed to dataPath as parameter: "[name1, name2, ...]"
   * - empty for single query source API calls
   * @type {string}
   */
  @observable schemePath = null;

  /**
   * external API URL path to get data. Parameters:
   * - "{0}": query source name (optional for single query source API calls)
   * - "{1}": top count
   * @type {null}
   */
  @observable dataPath = null;

  /**
   * Flatten arrays.
   * @type {boolean}
   */
  @observable flattenArrays = true;

  /**
   * Flatten objects.
   * @type {boolean}
   */
  @observable flattenObjects = true;

  /**
   * Flatten depth.
   * @type {number}
   */
  @observable flattenDepth = null;

  /**
   * Sub column separator string. Default value is ".".
   * @type {null}
   */
  @observable subColumnSeparator = null;

  /**
   * Request cache is turned on/off
   * @type {boolean}
   */
  @observable requestCacheEnabled = true;

  /**
   * Request cache time to live (in seconds)
   * @type {number}
   */
  @observable requestCacheTtl = 600;

  /**
   * Request headers and body settings
   * @type {IzendaRequest}
   */
  @observable request = new IzendaRequest([], '', IZENDA_REQUEST_BODY_LANGUAGES.TEXT);

  /**
   * ComposeConnection string for single endpoint.
   * @returns {string} Connection string for endpoint.
   */
  composeConnectionString() {
    const result = {};

    const addParam = (obj, key, value, defaultValue) => {
      if (isNil(value) || value === defaultValue)
        return null;
      obj[key] = value;
    };

    addParam(result, 'name', this.endpointName);
    addParam(result, 'address', this.address);
    addParam(result, 'datapath', this.dataPath);
    addParam(result, 'schemapath', this.schemePath, '');
    addParam(result, 'flattenarrays', this.flattenArrays, true);
    addParam(result, 'flattendepth', this.flattenDepth, DEFAULT_DEPTH);
    addParam(result, 'flattenobjects', this.flattenObjects, true);
    addParam(result, 'subcolumnseparator', this.subColumnSeparator, DEFAULT_SEPARATOR);
    addParam(result, 'cacheenabled', this.requestCacheEnabled, this.defaultCacheEnabled);
    addParam(result, 'cachettl', this.requestCacheTtl, this.defaultCacheTtl);

    // Append additional request headers
    if (this.request.headersCount > 0) {
      result.headers = [];
      each((header: IzendaRequestHeader) => {
        if(header.name){
          result.headers.push({
            name: header.name,
            value: header.value
          });
        }
      }, this.request.headers);
    }

    // Append request body
    if (this.request.body)
      addParam(result, 'bodycontent', this.request.bodyBase64);

    return result;
  }

  /**
   * Basic validation
   * @returns {string}
   */
  getValidationMessage(allEndpoints) {
    if (!this.endpointName || !this.dataPath || !this.address)
      return localizer.getText('CONNECTION_STRING_BUILDER_REST_VALIDATION1');
    if (!allEndpoints || allEndpoints.length < 2)
      return null;
    const otherEndpointWithSameName = find(x => x !== this && x.endpointName === this.endpointName, allEndpoints);
    if (!isNil(otherEndpointWithSameName)) {
      return localizer.getText('CONNECTION_STRING_BUILDER_REST_VALIDATION2');
    }
    return null;
  }

  /**
   * Apply preset to this endpoint
   * @param {object} preset preset data for restore.
   * @param {boolean} restoreEndpointName whether we need to restore endpoint name.
   */
  @action
  applyPreset(preset, restoreEndpointName) {
    if (restoreEndpointName)
      this.endpointName = preset.endpointName;
    this.address = preset.address;
    this.dataPath = preset.dataPath;
    this.schemePath = preset.schemePath;
    this.flattenArrays = preset.flattenArrays;
    if (preset.flattenDepth !== DEFAULT_DEPTH)
      this.flattenDepth = preset.flattenDepth;
    this.flattenObjects = preset.flattenObjects;
    if (preset.subColumnSeparator !== DEFAULT_SEPARATOR)
      this.subColumnSeparator = preset.subColumnSeparator;
    this.requestCacheEnabled = preset.requestCacheEnabled;
    this.requestCacheTtl = preset.requestCacheTtl;

    this.request.body = preset.body || '';
    if (!isNil(preset.headers)) {
      this.request.headers = map(
        k => new IzendaRequestHeader(true, k, preset.headers[k]),
        keys(preset.headers || []));
      this.request.addEmptyLastHeader();
    }
  }

  /**
   * Convert endpoint to preset object
   * @returns {Object}
   */
  toPreset() {
    return {
      endpointName: this.endpointName,
      address: this.address,
      dataPath: this.dataPath,
      schemePath: this.schemePath,
      flattenArrays: this.flattenArrays,
      flattenDepth: this.flattenDepth,
      flattenObjects: this.flattenObjects,
      subColumnSeparator: this.subColumnSeparator,
      requestCacheEnabled: this.requestCacheEnabled,
      requestCacheTtl: this.requestCacheTtl,
      body: this.request.body,
      headers: reduce((acc, curr) => {
        acc[curr.name] = curr.value;
        return acc;
      }, {}, this.request.headers)
    };
  }
}



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.ConnectionRestEndpoint.js